import { useEffect, useState } from 'react';
import {
  type OnboardFormState,
  PRACTICE_AREAS,
  THEMES,
} from './types';
import { deriveSlugFromFirmName } from '../../lib/onboard/tenantDiscovery';
import { HelpTip } from '../ui/HelpTip';

export interface FirmSetupStepProps {
  state: OnboardFormState;
  onChange: (patch: Partial<OnboardFormState>) => void;
  onSubmit: () => void;
  busy?: boolean;
}

// SharePoint site slugs: lowercase alphanumeric + hyphen, start/end
// with alphanumeric, 1–32 chars. The HTML `pattern` attribute only
// fires on `<form>` submit, but the wizard uses a button — so we
// must gate `canSubmit` at the JS layer. Without this, a user could
// type "Foo Bar" and the provisioner would later reject the site
// name (or worse, create a percent-encoded URL).
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/;

function slugValidationError(slug: string): string | null {
  const trimmed = slug.trim();
  if (trimmed.length === 0) return null; // empty is "not yet" — gated separately
  if (!SLUG_RE.test(trimmed)) {
    if (/[A-Z]/.test(trimmed)) {
      return 'Slug must be lowercase only.';
    }
    if (/\s/.test(trimmed)) {
      return 'Slug cannot contain spaces. Use hyphens instead.';
    }
    if (/^[-]/.test(trimmed) || /[-]$/.test(trimmed)) {
      return 'Slug cannot start or end with a hyphen.';
    }
    if (trimmed.length > 32) {
      return 'Slug must be 32 characters or fewer.';
    }
    return 'Slug can only contain lowercase letters, numbers, and hyphens.';
  }
  return null;
}

export function FirmSetupStep({
  state,
  onChange,
  onSubmit,
  busy,
}: FirmSetupStepProps) {
  const [slugTouched, setSlugTouched] = useState(state.slug.length > 0);

  // Auto-derive the slug from the firm name UNTIL the user manually
  // edits the slug field. After that, hands-off — they own it.
  useEffect(() => {
    if (slugTouched) return;
    const derived = deriveSlugFromFirmName(state.firmName);
    if (derived && derived !== state.slug) {
      onChange({ slug: derived });
    }
  }, [state.firmName, slugTouched, state.slug, onChange]);

  const slugError = slugValidationError(state.slug);

  const canSubmit =
    state.firmName.trim().length > 0 &&
    state.slug.trim().length > 0 &&
    slugError === null &&
    state.practiceArea.length > 0 &&
    !busy;

  return (
    <section
      data-testid="onboard-firm-step"
      className="flex flex-col gap-4 p-4"
    >
      <h2 className="text-lg font-bold text-black">Set up your firm</h2>
      <p className="text-sm text-black">
        Pick a name, slug, and practice area. NIPE may suggest values — you
        can change anything before saving.
      </p>

      <label className="flex flex-col gap-1 text-sm text-black">
        <span className="font-bold">Firm name</span>
        <input
          type="text"
          data-testid="onboard-input-firmName"
          value={state.firmName}
          onChange={(e) => onChange({ firmName: e.target.value })}
          className="border border-[#404040] bg-white px-2 py-1 text-black"
          placeholder="e.g. Morrison Legal LLC"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm text-black">
        <span className="font-bold">
          Portal slug{' '}
          <span className="text-xs font-normal text-gray-700">
            (used in URL: /c/{state.slug || 'your-slug'})
          </span>
        </span>
        <input
          type="text"
          data-testid="onboard-input-slug"
          value={state.slug}
          onChange={(e) => {
            setSlugTouched(true);
            onChange({ slug: e.target.value });
          }}
          aria-invalid={slugError !== null}
          aria-describedby={slugError ? 'onboard-slug-error' : undefined}
          className={`border ${
            slugError ? 'border-[#a00]' : 'border-[#404040]'
          } bg-white px-2 py-1 font-mono text-black`}
          placeholder="morrison-legal"
          pattern="[a-z0-9][a-z0-9-]{0,30}[a-z0-9]"
        />
        {slugError && (
          <span
            id="onboard-slug-error"
            data-testid="onboard-slug-error"
            className="text-xs font-semibold text-[#a00]"
          >
            {slugError}
          </span>
        )}
      </label>

      <label className="flex flex-col gap-1 text-sm text-black">
        <span className="font-bold">Practice area</span>
        <select
          data-testid="onboard-input-practiceArea"
          value={state.practiceArea}
          onChange={(e) => onChange({ practiceArea: e.target.value })}
          className="border border-[#404040] bg-white px-2 py-1 text-black"
        >
          <option value="">Select…</option>
          {PRACTICE_AREAS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm text-black">
        <span className="font-bold">Theme</span>
        <select
          data-testid="onboard-input-theme"
          value={state.theme}
          onChange={(e) => onChange({ theme: e.target.value })}
          className="border border-[#404040] bg-white px-2 py-1 text-black"
        >
          <option value="">Select…</option>
          {THEMES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm text-black">
        <span className="font-bold">Primary color</span>
        <input
          type="color"
          data-testid="onboard-input-primaryColor"
          value={state.primaryColor}
          onChange={(e) => onChange({ primaryColor: e.target.value })}
          className="h-9 w-20 border border-[#404040]"
        />
      </label>

      <HelpTip k="onboard.firmSubmit" wrap>
        <button
          type="button"
          data-testid="onboard-firm-submit"
          data-xp-default
          onClick={onSubmit}
          disabled={!canSubmit}
          // XP "default-button" affordance via xp-luna-overrides (cream
          // gradient + thicker navy border). Tailwind `bg-[#2e7d32]
          // text-white` gets clobbered by xp.css `button{background:#ece9d8}`,
          // rendering unreadable white-on-grey.
          className="self-start px-4 py-1 text-sm font-bold text-black cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Set up my firm
        </button>
      </HelpTip>
    </section>
  );
}
